import { useEffect, useState } from 'react';
import { members, invites, type Member, type Invite, type Role } from '../../api';
import { useAuth, activeRole } from '../../stores/auth';

type Tab = 'members' | 'invites';

export function MembersPage() {
  const [tab, setTab] = useState<Tab>('members');
  return (
    <div>
      <div className="toolbar"><h1>Members</h1></div>
      <div style={{ display: 'flex', gap: '0.5em', marginBottom: '1em', borderBottom: '1px solid var(--border)' }}>
        <TabButton active={tab === 'members'} onClick={() => setTab('members')}>Members</TabButton>
        <TabButton active={tab === 'invites'} onClick={() => setTab('invites')}>Pending invites</TabButton>
      </div>
      {tab === 'members' ? <MembersTab /> : <InvitesTab />}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      style={{
        background: 'transparent', border: 'none',
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        borderRadius: 0, color: active ? 'var(--fg-1)' : 'var(--fg-3)',
        padding: '0.6em 1em', marginBottom: '-1px', cursor: 'pointer',
      }}>{children}</button>
  );
}

function MembersTab() {
  const myUserId = useAuth((s) => s.userId);
  const myRole = activeRole();
  const [list, setList] = useState<Member[]>([]);
  const [editing, setEditing] = useState<Member | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try { setList((await members.list()).members); }
    catch { setErr('Failed to load members'); }
  }
  useEffect(() => { refresh(); }, []);

  async function remove(m: Member) {
    if (!confirm(`Remove ${m.email} from this org?`)) return;
    try {
      await members.remove(m.id);
      refresh();
    } catch (e: unknown) {
      setErr(((e as { detail?: { error?: string } }).detail?.error) || 'Failed to remove');
    }
  }

  return (
    <div>
      {err && <div className="error">{err}</div>}
      {list.length === 0 ? (
        <div className="empty">No members. Invite someone from the Pending invites tab.</div>
      ) : (
        <table>
          <thead>
            <tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Last login</th><th></th></tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id}>
                <td><strong>{m.email}</strong>
                  {m.id === myUserId && <span style={{ color: 'var(--fg-3)', marginLeft: '0.4em', fontSize: '0.85em' }}>(you)</span>}
                  {m.is_platform_admin && <span style={{ color: 'var(--accent)', marginLeft: '0.4em', fontSize: '0.78em' }}>· platform admin</span>}
                </td>
                <td>{m.name || '—'}</td>
                <td><RoleBadge role={m.role} /></td>
                <td>{m.is_active ? <span className="badge active">active</span> : <span className="badge offline">disabled</span>}</td>
                <td>{m.last_login_at ? new Date(m.last_login_at).toLocaleString() : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => setEditing(m)}>Edit role</button>{' '}
                  <button className="danger" disabled={m.id === myUserId} onClick={() => remove(m)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <EditRoleModal
          member={editing}
          myRole={myRole}
          isSelf={editing.id === myUserId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const colors: Record<Role, string> = {
    owner: 'rgba(74,222,128,0.15)',
    admin: 'rgba(56,189,248,0.15)',
    editor: 'rgba(245,158,11,0.15)',
    viewer: 'rgba(107,120,148,0.2)',
  };
  const text: Record<Role, string> = {
    owner: 'var(--accent-2)',
    admin: 'var(--accent)',
    editor: 'var(--warn)',
    viewer: 'var(--fg-2)',
  };
  return <span className="badge" style={{ background: colors[role], color: text[role] }}>{role}</span>;
}

function EditRoleModal({
  member, myRole, isSelf, onClose, onSaved,
}: { member: Member; myRole: Role | null; isSelf: boolean; onClose: () => void; onSaved: () => void }) {
  const [role, setRole] = useState<Role>(member.role);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canEdit = !isSelf && (myRole === 'owner' || (myRole === 'admin' && member.role !== 'owner'));
  const availableRoles: Role[] = myRole === 'owner' ? ['owner', 'admin', 'editor', 'viewer'] : ['admin', 'editor', 'viewer'];

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      if (role !== member.role) await members.updateRole(member.id, role);
      onSaved();
    } catch (e: unknown) {
      setErr(((e as { detail?: { error?: string } }).detail?.error) || 'Update failed');
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: 420 }}>
        <h2 style={{ marginTop: 0 }}>Edit {member.email}</h2>
        <div className="field">
          <label>Role</label>
          <select value={role} disabled={!canEdit} onChange={(e) => setRole(e.target.value as Role)}>
            {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {!canEdit && (
            <div style={{ color: 'var(--fg-3)', fontSize: '0.82em', marginTop: '0.3em' }}>
              {isSelf ? 'You cannot change your own role.' : 'You do not have permission to change this role.'}
            </div>
          )}
        </div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !canEdit} onClick={submit}>{busy ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function InvitesTab() {
  const [list, setList] = useState<Invite[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [lastClaimUrl, setLastClaimUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try { setList((await invites.list()).invites); }
    catch { setErr('Failed to load invites'); }
  }
  useEffect(() => { refresh(); }, []);

  async function revoke(id: string) {
    if (!confirm('Revoke this pending invite?')) return;
    await invites.revoke(id);
    refresh();
  }

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: '1em' }}>
        <div style={{ flex: 1 }} />
        <button className="primary" onClick={() => setShowCreate(true)}>+ New invite</button>
      </div>
      {err && <div className="error">{err}</div>}
      {lastClaimUrl && (
        <div className="card" style={{ background: 'var(--bg-2)', borderColor: 'var(--accent)' }}>
          <strong>Invite created</strong>
          <div style={{ marginTop: '0.4em', color: 'var(--fg-2)', fontSize: '0.9em' }}>Share this link with the invitee:</div>
          <code style={{ display: 'block', padding: '0.6em', marginTop: '0.4em', background: 'var(--bg-0)', borderRadius: 4, wordBreak: 'break-all' }}>
            {lastClaimUrl}
          </code>
          <button style={{ marginTop: '0.6em' }} onClick={() => navigator.clipboard.writeText(lastClaimUrl)}>Copy</button>
          <button style={{ marginTop: '0.6em', marginLeft: '0.4em' }} onClick={() => setLastClaimUrl(null)}>Dismiss</button>
        </div>
      )}
      {list.length === 0 ? (
        <div className="empty">No pending invites.</div>
      ) : (
        <table>
          <thead><tr><th>Email</th><th>Role</th><th>Expires</th><th>Invited by</th><th>Sent</th><th></th></tr></thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id}>
                <td>{i.email}</td>
                <td><RoleBadge role={i.role} /></td>
                <td>{new Date(i.expires_at).toLocaleString()}</td>
                <td>{i.invited_by_email ?? '—'}</td>
                <td>{new Date(i.created_at).toLocaleDateString()}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="danger" onClick={() => revoke(i.id)}>Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showCreate && (
        <CreateInviteModal
          onClose={() => setShowCreate(false)}
          onCreated={(url) => { setShowCreate(false); setLastClaimUrl(url); refresh(); }}
        />
      )}
    </div>
  );
}

function CreateInviteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (url: string) => void }) {
  const myRole = activeRole();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('editor');
  const [days, setDays] = useState(7);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const availableRoles: Array<'admin' | 'editor' | 'viewer'> =
    myRole === 'owner' ? ['admin', 'editor', 'viewer'] : ['editor', 'viewer'];

  async function submit() {
    setErr(null); setBusy(true);
    try {
      const r = await invites.create({ email, role, expiresInHours: days * 24 });
      onCreated(r.claimUrl);
    } catch (e: unknown) {
      const detail = ((e as { detail?: { error?: string } }).detail?.error);
      if (detail === 'already_a_member') setErr('This user is already a member of this org.');
      else if (detail === 'invite_already_pending') setErr('A pending invite already exists for this email.');
      else setErr('Create failed.');
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: 460 }}>
        <h2 style={{ marginTop: 0 }}>Invite a new member</h2>
        <p style={{ color: 'var(--fg-3)', fontSize: '0.88em' }}>
          Generates a one-time claim link. The invitee opens it, sets a password (if they're new),
          and joins this org immediately.
        </p>
        <div className="field"><label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required /></div>
        <div className="field"><label>Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select></div>
        <div className="field"><label>Expires in (days)</label>
          <input type="number" value={days} min={1} max={30} onChange={(e) => setDays(Number(e.target.value))} /></div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!email || busy} onClick={submit}>
            {busy ? 'Creating...' : 'Create invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
